/* Emergency Leash accessibility toolbar. Self-hosted: builds the widget,
   toggles el-a11y-* classes on <html>, persists choices in localStorage.
   An inline script in layout/theme.liquid re-applies saved settings before
   first paint; this file only has to keep the UI and storage in sync.

   Storage is an object: {modes:[...], text:<zoom>, line:<line-height>}.
   Older builds stored a bare array of mode names - load() migrates those. */
(function () {
  'use strict';
  var KEY = 'el-a11y';

  /* Text size and line spacing are stepped, not on/off. Stored as the raw CSS
     value (not the index) so the pre-paint script can apply it without needing
     these tables. text 1 / line 0 mean "untouched". */
  var TEXT_STEPS = [1, 1.1, 1.25, 1.5, 1.75, 2];
  var LINE_STEPS = [0, 1.6, 1.9, 2.2];
  var LINE_LABELS = ['Normal', 'Relaxed', 'Loose', 'Extra loose'];

  /* Panel sections. Keys are kept stable where a mode already existed, so
     saved settings survive: font, contrast, links, motion, cursor. */
  var SECTIONS = [
    { title: 'Text', modes: [
      ['font',      'Readable font'],
      ['dyslexia',  'Dyslexia friendly'],
      ['spacing',   'Wider letter spacing'],
      ['alignleft', 'Align text left']
    ] },
    { title: 'Color', modes: [
      ['contrast',  'High contrast'],
      ['invert',    'Invert colors'],
      ['greyscale', 'Greyscale'],
      ['lowsat',    'Low saturation']
    ] },
    { title: 'Navigation', modes: [
      ['links',       'Highlight links'],
      ['cursor',      'Large cursor'],
      ['focus',       'Strong focus outline'],
      ['motion',      'Stop animations'],
      ['hideimg',     'Hide images'],
      ['tooltips',    'Show tooltips'],
      ['readingline', 'Reading line'],
      ['readingmask', 'Reading mask']
    ] }
  ];

  var ALL_MODES = SECTIONS.reduce(function (acc, s) {
    return acc.concat(s.modes.map(function (m) { return m[0]; }));
  }, []);

  /* Modes that fight each other if combined - each set drives the same CSS
     property, so turning one on turns its siblings off rather than letting the
     cascade pick a winner at random. */
  var EXCLUSIVE = [
    ['font', 'dyslexia'],                          // font-family
    ['contrast', 'invert', 'greyscale', 'lowsat'], // color / filter
    ['readingline', 'readingmask']                 // pointer-following overlay
  ];

  function defaults() { return { modes: [], text: 1, line: 0 }; }

  function load() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return defaults(); }
    if (!raw) return defaults();
    var s = defaults();
    if (Array.isArray(raw)) {
      // Legacy format: bare array of modes, with "bigtext" instead of a stepper.
      s.modes = raw.filter(function (m) { return ALL_MODES.indexOf(m) !== -1; });
      if (raw.indexOf('bigtext') !== -1) s.text = 1.25;
      return s;
    }
    if (Array.isArray(raw.modes)) {
      s.modes = raw.modes.filter(function (m) { return ALL_MODES.indexOf(m) !== -1; });
    }
    if (TEXT_STEPS.indexOf(raw.text) !== -1) s.text = raw.text;
    if (LINE_STEPS.indexOf(raw.line) !== -1) s.line = raw.line;
    return s;
  }

  function save(s) {
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  function apply(s) {
    var r = document.documentElement;
    var want = {};
    s.modes.forEach(function (m) { want['el-a11y-' + m] = 1; });
    if (s.text > 1) want['el-a11y-textsize'] = 1;
    if (s.line > 0) want['el-a11y-linespacing'] = 1;

    /* Drop every el-a11y-* class we no longer want, rather than only toggling
       the ones in SECTIONS. The pre-paint script adds classes straight from
       storage without validating them, so this is what cleans up modes that
       were renamed or removed in a later build (e.g. the legacy "bigtext"). */
    Array.prototype.slice.call(r.classList).forEach(function (c) {
      if (c.indexOf('el-a11y-') === 0 && !want[c]) r.classList.remove(c);
    });
    Object.keys(want).forEach(function (c) { r.classList.add(c); });

    r.style.setProperty('--elA11yZoom', String(s.text));
    if (s.line > 0) r.style.setProperty('--elA11yLine', String(s.line));
    trackPointer();
  }

  // Must be declared before the first apply(), which calls trackPointer():
  // otherwise the restore below attaches the listener, this line then resets
  // the flag to false, and the listener can never be removed again.
  var tracking = false;
  var state = load();
  apply(state);

  // ---- Build the widget ----
  function toggleHtml(m) {
    return '<button type="button" class="el-a11y__toggle" role="switch" aria-checked="false" data-mode="' +
      m[0] + '">' + m[1] + '</button>';
  }
  function stepperHtml(key, label, value) {
    return '<div class="el-a11y__row">' +
        '<span class="el-a11y__row-label" id="ElA11y' + key + 'Label">' + label + '</span>' +
        '<span class="el-a11y__step">' +
          '<button type="button" class="el-a11y__step-btn" data-step="' + key + '" data-dir="-1"' +
            ' aria-label="Decrease ' + label.toLowerCase() + '">&minus;</button>' +
          '<span class="el-a11y__step-val" data-val="' + key + '" role="status">' + value + '</span>' +
          '<button type="button" class="el-a11y__step-btn" data-step="' + key + '" data-dir="1"' +
            ' aria-label="Increase ' + label.toLowerCase() + '">+</button>' +
        '</span>' +
      '</div>';
  }

  var sectionsHtml = SECTIONS.map(function (sec) {
    var body = '';
    if (sec.title === 'Text') {
      body += stepperHtml('text', 'Text size', Math.round(state.text * 100) + '%') +
              stepperHtml('line', 'Line spacing', LINE_LABELS[LINE_STEPS.indexOf(state.line)]);
    }
    body += '<div class="el-a11y__grid">' + sec.modes.map(toggleHtml).join('') + '</div>';
    return '<section class="el-a11y__section" aria-labelledby="ElA11ySec' + sec.title + '">' +
        '<h3 class="el-a11y__section-title" id="ElA11ySec' + sec.title + '">' + sec.title + '</h3>' +
        body +
      '</section>';
  }).join('');

  var root = document.createElement('div');
  root.className = 'el-a11y';
  root.innerHTML =
    '<button type="button" class="el-a11y__btn" aria-expanded="false" aria-controls="ElA11yPanel" aria-label="Accessibility options">' +
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">' +
        '<circle cx="12" cy="4.2" r="2.2"/>' +
        '<path d="M12 7.2c-.6 0-4.8-.55-6.6-.8a1 1 0 1 0-.3 1.98c1.5.22 4 .55 5.1.68v2.9l-2.1 6.9a1 1 0 0 0 1.9.6l1.8-5.4h.4l1.8 5.4a1 1 0 0 0 1.9-.6l-2.1-6.9v-2.9c1.1-.13 3.6-.46 5.1-.68a1 1 0 1 0-.3-1.98c-1.8.25-6 .8-6.6.8z"/>' +
      '</svg>' +
    '</button>' +
    '<div class="el-a11y__panel" id="ElA11yPanel" role="dialog" aria-label="Accessibility options" hidden>' +
      '<div class="el-a11y__head">' +
        '<span class="el-a11y__title">Accessibility</span>' +
        '<button type="button" class="el-a11y__close" aria-label="Close accessibility options">&#10005;</button>' +
      '</div>' +
      '<div class="el-a11y__body">' + sectionsHtml + '</div>' +
      '<div class="el-a11y__foot">' +
        '<button type="button" class="el-a11y__reset">Reset all</button>' +
        '<button type="button" class="el-a11y__stmt-open">Accessibility statement</button>' +
        '<p class="el-a11y__note">Settings are saved on this device.</p>' +
      '</div>' +
    '</div>' +
    '<div class="el-a11y__overlay" hidden>' +
      '<div class="el-a11y__stmt" role="dialog" aria-modal="true" aria-labelledby="ElA11yStmtTitle">' +
        '<div class="el-a11y__head">' +
          '<span class="el-a11y__title" id="ElA11yStmtTitle">Accessibility statement</span>' +
          '<button type="button" class="el-a11y__close el-a11y__stmt-close" aria-label="Close accessibility statement">&#10005;</button>' +
        '</div>' +
        '<div class="el-a11y__stmt-body">' +
          '<p>Emergency Leash is committed to ensuring that our website is accessible to everyone, including people with disabilities. We want every visitor to be able to shop, learn about our products, and use our lost-pet services with ease.</p>' +
          '<p>We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA, the standard referenced by the Americans with Disabilities Act (ADA). Measures we have taken include:</p>' +
          '<ul>' +
            '<li>An on-site accessibility toolbar offering stepped text sizing and line spacing; readable and dyslexia-friendly fonts; wider letter spacing and left-aligned text; high contrast, inverted, greyscale and low-saturation color modes; highlighted links, a large cursor, a strong focus outline, stopped animations, hidden images, visible tooltips, and a reading line or reading mask</li>' +
            '<li>Text alternatives for images and meaningful labels for controls</li>' +
            '<li>Full keyboard operability with clearly visible focus indicators</li>' +
            '<li>Animations that respect your device’s reduced-motion setting</li>' +
          '</ul>' +
          '<p>Accessibility is an ongoing effort, and we review our site regularly as it evolves. Despite our best efforts, some content may not yet be fully accessible.</p>' +
          '<p><strong>Feedback:</strong> if you experience any difficulty using this website, or have suggestions on how we can improve accessibility, please contact us at <a href="mailto:info@emergencyleash.com">info@emergencyleash.com</a>. Please describe the page and the barrier you encountered; we take every report seriously and will make reasonable efforts to address it promptly.</p>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="el-a11y__readline" aria-hidden="true"></div>' +
    '<div class="el-a11y__readmask" aria-hidden="true"></div>';
  document.body.appendChild(root);

  var btn = root.querySelector('.el-a11y__btn');
  var panel = root.querySelector('.el-a11y__panel');
  var toggles = root.querySelectorAll('.el-a11y__toggle');

  function syncUi() {
    toggles.forEach(function (t) {
      t.setAttribute('aria-checked', state.modes.indexOf(t.getAttribute('data-mode')) !== -1 ? 'true' : 'false');
    });
    root.querySelector('[data-val="text"]').textContent = Math.round(state.text * 100) + '%';
    root.querySelector('[data-val="line"]').textContent = LINE_LABELS[LINE_STEPS.indexOf(state.line)];
    // Grey out a stepper button at the end of its range instead of no-op clicks.
    root.querySelectorAll('.el-a11y__step-btn').forEach(function (b) {
      var steps = b.getAttribute('data-step') === 'text' ? TEXT_STEPS : LINE_STEPS;
      var cur = b.getAttribute('data-step') === 'text' ? state.text : state.line;
      var next = steps.indexOf(cur) + Number(b.getAttribute('data-dir'));
      b.setAttribute('aria-disabled', next < 0 || next >= steps.length ? 'true' : 'false');
    });
  }

  function commit() { apply(state); save(state); syncUi(); }

  function openPanel() {
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    // Comma selectors match in document order, so this would otherwise land on
    // the decrease-text button even when it is greyed out at the low end.
    var first = panel.querySelector('.el-a11y__step-btn:not([aria-disabled="true"]), .el-a11y__toggle');
    if (first) first.focus();
  }
  function closePanel(refocus) {
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (refocus) btn.focus();
  }

  btn.addEventListener('click', function () {
    panel.hidden ? openPanel() : closePanel(true);
  });
  root.querySelector('.el-a11y__close').addEventListener('click', function () { closePanel(true); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !panel.hidden) closePanel(true);
  });
  document.addEventListener('click', function (e) {
    if (!panel.hidden && !root.contains(e.target)) closePanel(false);
  });

  toggles.forEach(function (t) {
    t.addEventListener('click', function () {
      var mode = t.getAttribute('data-mode');
      var i = state.modes.indexOf(mode);
      if (i !== -1) {
        state.modes.splice(i, 1);
      } else {
        // Drop any sibling from the same exclusive set before switching on.
        EXCLUSIVE.forEach(function (set) {
          if (set.indexOf(mode) === -1) return;
          state.modes = state.modes.filter(function (m) { return set.indexOf(m) === -1; });
        });
        state.modes.push(mode);
      }
      commit();
    });
  });

  root.querySelectorAll('.el-a11y__step-btn').forEach(function (b) {
    b.addEventListener('click', function () {
      var isText = b.getAttribute('data-step') === 'text';
      var steps = isText ? TEXT_STEPS : LINE_STEPS;
      var cur = isText ? state.text : state.line;
      var next = steps.indexOf(cur) + Number(b.getAttribute('data-dir'));
      if (next < 0 || next >= steps.length) return;
      if (isText) state.text = steps[next]; else state.line = steps[next];
      commit();
    });
  });

  root.querySelector('.el-a11y__reset').addEventListener('click', function () {
    state = defaults();
    commit();
  });

  // ---- Reading line / reading mask ----
  // Both follow the pointer via a single CSS variable; the listener is only
  // attached while one of them is on, so there is no idle mousemove cost.
  // `tracking` is declared up by the first apply() call -- see the note there.
  function onMove(e) {
    document.documentElement.style.setProperty('--elA11yY', e.clientY + 'px');
  }
  function trackPointer() {
    var need = state.modes.indexOf('readingline') !== -1 || state.modes.indexOf('readingmask') !== -1;
    if (need && !tracking) {
      document.addEventListener('pointermove', onMove, { passive: true });
      tracking = true;
    } else if (!need && tracking) {
      document.removeEventListener('pointermove', onMove);
      tracking = false;
    }
  }

  // ---- Accessibility statement dialog ----
  // Opened from the panel button or any link to #accessibility-statement
  // (e.g. the footer), so the statement needs no separate Shopify page.
  var overlay = root.querySelector('.el-a11y__overlay');
  var stmtReturnFocus = null;
  function openStmt() {
    stmtReturnFocus = document.activeElement;
    closePanel(false);
    overlay.hidden = false;
    root.querySelector('.el-a11y__stmt-close').focus();
  }
  function closeStmt() {
    overlay.hidden = true;
    if (stmtReturnFocus && stmtReturnFocus.focus) stmtReturnFocus.focus();
  }
  root.querySelector('.el-a11y__stmt-open').addEventListener('click', openStmt);
  root.querySelector('.el-a11y__stmt-close').addEventListener('click', closeStmt);
  overlay.addEventListener('click', function (e) { if (e.target === overlay) closeStmt(); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !overlay.hidden) closeStmt();
  });
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href="#accessibility-statement"]');
    if (a) { e.preventDefault(); openStmt(); }
  });

  syncUi();
})();
